import { useEffect, useMemo, useState } from 'react';
import styles from './attendance.module.css';
import PrimaryButton from '../Buttons/PrimaryButton';
import SecondaryButton from '../Buttons/SecondaryButton';
import { saveEmployeeAttendance, getTherapistTimings, getTherapistBreaks, createBreak, updateBreak, deleteBreak, getLeaveStatuses, getBookingsList } from '../../APICalling/APICalling';
import { useGlobalContext } from '../../Context/GlobalContext';
import {
  buildTimeDropdownOptions,
  doesTimeRangeCrossMidnight,
  formatDateToDDMMYYYY,
  getExtendedDropdownSelectedValue,
  getOutletExtendedFlagForDate,
  parseExtendedDropdownValue,
} from '../../Utils/utils';
import EditIcon from '../../Assets/images/EditIcon.svg';
import DeleteIcon from '../../Assets/images/delete.svg';
import DropdownCustomDate from '../DropDown/DropdownCustomDate';
import DropdownReason from '../DropDown/DropdownReason';
import Loader from '../Loader/Loader';

const parseTimeStringToDate = (timeString) => {
  try {
    if (!timeString || typeof timeString !== 'string') return null;
    const today = new Date();
    const [h, m, s] = timeString.split(':').map(Number);
    const d = new Date(today);
    d.setHours(Number(h) || 0, Number(m) || 0, Number(s) || 0, 0);
    return d;
  } catch {
    return null;
  }
};
const formatTimeToHHMM = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return '';
  const parts = timeString.split(':');
  if (parts.length < 2) return '';
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
};

const convertBreakApiTimeTo24Hour = (time12h) => {
  if (!time12h || typeof time12h !== 'string') return '';

  const match = time12h.match(/^(\d{1,2}):(\d{2}):(AM|PM)$/i);
  if (!match) return '';

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();

  if (hours >= 13) {
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

const parseBreakApiDateTime = (dateTimeString) => {
  if (!dateTimeString || typeof dateTimeString !== 'string') return null;

  const [datePart, timePart] = dateTimeString.trim().split(' ');
  if (!datePart || !timePart) return null;

  const time24 = convertBreakApiTimeTo24Hour(timePart);
  if (!time24) return null;

  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;

  const [hours, minutes] = time24.split(':').map(Number);
  const parsed = new Date(year, month - 1, day, hours || 0, minutes || 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const convertToMinutes = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') return Number.NaN;
  const [hours, minutes] = timeValue.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return Number.NaN;
  return (hours * 60) + minutes;
};

const isFutureDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate > today;
};

const isPastDate = (date) => {
  if (!date) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

const Attendance = ({ 
  staff, 
  onCheckIn, 
  onCheckOut,
  onAddBreak,
  onEditBreak,
  onDeleteBreak,
  onClose,
}) => {
  const { selectedOutlet, selectedDate, selectedOutletData } = useGlobalContext();
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [isCheckedOut, setIsCheckedOut] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [isCheckInExpanded, setIsCheckInExpanded] = useState(0);
  const [isCheckOutExpanded, setIsCheckOutExpanded] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);
  const [isBreakListLoading, setIsBreakListLoading] = useState(false);
  const [breaksData, setBreaksData] = useState([]);
  const [isAddBreakMode, setIsAddBreakMode] = useState(false);
  const [editingBreakId, setEditingBreakId] = useState(null);
  const [reasonOptions, setReasonOptions] = useState([]);
  const [overlapWarning, setOverlapWarning] = useState('');
  const [therapistBookings, setTherapistBookings] = useState([]);
  
  // Break form states
  const [breakDate, setBreakDate] = useState(null);
  const [breakTime, setBreakTime] = useState('');
  const [breakDuration, setBreakDuration] = useState('');
  const [breakReason, setBreakReason] = useState('');
  const [breakReasonRemark, setBreakReasonRemark] = useState('');
  const [scheduledCheckIn, setScheduledCheckIn] = useState(null);
  const [scheduledCheckOut, setScheduledCheckOut] = useState(null);
  
  // Error states for validation
  const [dateError, setDateError] = useState('');
  const [timeError, setTimeError] = useState('');
  const [durationError, setDurationError] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [durationExceedsError, setDurationExceedsError] = useState('');
  const [checkInError, setCheckInError] = useState('');

  // Fetch staff schedule when component mounts
  useEffect(() => {
    if (staff?.id) {
      setCheckInError('');
      fetchStaffSchedule();
      fetchBreaksData();
    }
  }, [staff?.id]);

  // Fetch leave statuses on component mount
  useEffect(() => {
    fetchLeaveStatuses();
  }, []);

  // Fetch therapist bookings when break form is active and date changes
  useEffect(() => {
    if (isAddBreakMode && staff?.id && breakDate) {
      fetchTherapistBookings();
    }
  }, [isAddBreakMode, staff?.id, breakDate]);

  const fetchTherapistBookings = async () => {
    if (!staff?.id || !breakDate) return;
    
    try {
      // breakDate is in DD-MM-YYYY format
      const formattedDate = breakDate; // Already in DD-MM-YYYY format
      const response = await getBookingsList({
        pagination: 0,
        outlet: selectedOutlet,
        daterange: `${formattedDate} / ${formattedDate}`,
      });

      if (response?.data?.success && response?.data?.data?.list?.bookings) {
        const bookings = response.data.data.list.bookings;
        // Filter bookings for this therapist; exclude cancelled / no-show / voided
        const EXCLUDED_STATUSES = ['no-show', 'cancelled', 'voided'];
        const therapistBookingsList = [];
        bookings.forEach(booking => {
          const status = String(booking.status || '').toLowerCase();
          if (EXCLUDED_STATUSES.includes(status)) return;

          if (booking.booking_item) {
            Object.values(booking.booking_item).forEach(items => {
              items.forEach(item => {
                if (item.therapist_id === staff.id) {
                  therapistBookingsList.push({
                    id: item.id,
                    service_at: item.service_at,
                    duration: item.duration || 30,
                    service: item.service || 'Service',
                    status: booking.status,
                  });
                }
              });
            });
          }
        });
        setTherapistBookings(therapistBookingsList);
      } else {
        setTherapistBookings([]);
      }
    } catch (error) {
      console.error('Error fetching therapist bookings:', error);
      setTherapistBookings([]);
    }
  };

  const fetchStaffSchedule = async () => {
    if (!staff?.id) return;
    
    setIsLoadingSchedule(true);
    try {
      const apiData = {
        outlet: selectedOutlet, // Use logged outlet ID or fallback to 1
        start_date: formatDateToDDMMYYYY(selectedDate),
        end_date: formatDateToDDMMYYYY(selectedDate),
        leave: 0
      };
      const response = await getTherapistTimings(apiData);
      if (response && response.success && response.data) {
        const staffId = String(staff.id);
        const staffSchedule = response.data.list ? response.data.list[staffId] : undefined;
        // Extract scheduled and actual daily times for this staff and date
        if (staffSchedule && staffSchedule[formatDateToDDMMYYYY(selectedDate)]) {
          const schedule = staffSchedule[formatDateToDDMMYYYY(selectedDate)][0];
          if (schedule) {
            // Check if schedule has valid checkin and checkout times
            const hasValidSchedule = schedule.checkin && schedule.checkout;
            
            // Actual times (daily)
            const actualIn = schedule.checkin_daily ? parseTimeStringToDate(schedule.checkin_daily) : null;
            const actualOut = schedule.checkout_daily ? parseTimeStringToDate(schedule.checkout_daily) : null;
            setCheckInTime(actualIn);
            setCheckOutTime(actualOut);
            setIsCheckedIn(Boolean(actualIn));
            setIsCheckedOut(Boolean(actualOut));
            setIsCheckInExpanded(schedule?.is_checkin_calendar_expansion || 0);
            setIsCheckOutExpanded(schedule?.is_checkout_calendar_expansion || 0);
            
            // Store scheduled times for time slot generation
            if (hasValidSchedule) {
              setScheduledCheckIn(schedule.checkin);
              setScheduledCheckOut(schedule.checkout);
            } else {
              // No valid schedule
              setScheduledCheckIn(null);
              setScheduledCheckOut(null);
            }
          } else {
            // No schedule data
            setCheckInTime(null);
            setCheckOutTime(null);
            setIsCheckedIn(false);
            setIsCheckedOut(false);
            setIsCheckInExpanded(0);
            setIsCheckOutExpanded(0);
            setScheduledCheckIn(null);
            setScheduledCheckOut(null);
          }
        } else {
          // No schedule for this date
          setCheckInTime(null);
          setCheckOutTime(null);
          setIsCheckedIn(false);
          setIsCheckedOut(false);
          setIsCheckInExpanded(0);
          setIsCheckOutExpanded(0);
          setScheduledCheckIn(null);
          setScheduledCheckOut(null);
        }
      } else {
        // No schedule for staff
        setCheckInTime(null);
        setCheckOutTime(null);
        setIsCheckedIn(false);
        setIsCheckedOut(false);
        setIsCheckInExpanded(0);
        setIsCheckOutExpanded(0);
        setScheduledCheckIn(null);
        setScheduledCheckOut(null);
      }
    } catch (error) {
      console.error('Error fetching staff schedule:', error);
      setIsCheckInExpanded(0);
      setIsCheckOutExpanded(0);
      setScheduledCheckIn(null);
      setScheduledCheckOut(null);
    } finally {
      setIsLoadingSchedule(false);
    }
  };


  const fetchBreaksData = async () => {
    if (!staff?.id) return;
    
    setIsBreakListLoading(true);
    try {
        // Fetch breaks only for the selected date
        const formattedDate = formatDateToDDMMYYYY(selectedDate);
        const dateRange = `${formattedDate} 00:00 / ${formattedDate} 23:59`;
        const isCalendarExpansion = getOutletExtendedFlagForDate(selectedOutletData?.timing, selectedDate) ? 1 : 0;
        
        const response = await getTherapistBreaks({ 
            pagination: 0,
            daterange: dateRange,
            therapist_id: staff.id,
            selected_date: formattedDate,
            is_calendar_expansion: isCalendarExpansion,
        });

        
        if (response?.data?.success && response?.data?.data?.list?.leave) {
            setBreaksData(response.data.data.list.leave);
        } else {
            setBreaksData([]);
        }
    } catch (error) {
        console.error('Error fetching breaks data:', error);
        setBreaksData([]);
    } finally {
        setIsBreakListLoading(false);
    }
  };

  const fetchLeaveStatuses = async () => {
    try {
      const response = await getLeaveStatuses({ pagination: 0, type: 'break' });
      
      // Handle the nested response structure
      if (response?.data?.success && response?.data?.data?.list?.statuses) {
        // Map the statuses to dropdown format with both id and name
        const options = response.data.data.list.statuses.map(status => ({
          text: status.name,
          value: status.name,
          id: status.id  // Store the id as well
        }));
        setReasonOptions(options);
        // console.log("Reason options set:", options);
      } else {
        console.warn("API response structure unexpected, using fallback options");
        // Fallback to default options if API fails
        setReasonOptions([
          { text: 'OFF', value: 'OFF', id: null },
          { text: 'AL', value: 'AL', id: null },
          { text: 'MC', value: 'MC', id: null },
          { text: 'UL', value: 'UL', id: null },
        ]);
      }
    } catch (error) {
      console.error('Error fetching leave statuses:', error);
      // Fallback to default options
      setReasonOptions([
        { text: 'OFF', value: 'OFF', id: null },
        { text: 'AL', value: 'AL', id: null },
        { text: 'MC', value: 'MC', id: null },
        { text: 'UL', value: 'UL', id: null },
      ]);
    }
  };

  const handleCheckIn = async () => {
  if (!isCheckedIn && !isCheckedOut && !isLoading) {
    if (isFutureDate(selectedDate)) {
      setCheckInError('Cannot check-in for future date.');
      return;
    }
    if (isPastDate(selectedDate)) {
      setCheckInError("Cannot check in for past date");
      return;
    }
    setCheckInError('');
    setIsLoading(true);
    try {
      const attendanceData = {
        user: staff.id,
        type: "checkin",
        event_date: formatDateToDDMMYYYY(selectedDate),
        is_calendar_expansion: getAttendanceExpansionFlag(formatTime(new Date())),
      };
      const response = await saveEmployeeAttendance(attendanceData);
      const isSuccess = response?.success || response?.data?.success;
      const updated =
        (response?.data && typeof response.data === 'object' && !Array.isArray(response.data) && response.data.checkin_daily)
          ? response.data
          : response?.data?.data;

      if (isSuccess && updated) {
        onCheckIn && onCheckIn(updated);

        setCheckInTime(parseTimeStringToDate(updated.checkin_daily));
        setIsCheckedIn(true);
        setIsCheckInExpanded(
          updated?.is_checkin_calendar_expansion ??
          getAttendanceExpansionFlag(formatTime(new Date()))
        );
        setCheckInError('');

        try { window.dispatchEvent(new Event('staffTimingUpdate')); } catch {}
      } else {
        const apiMessage =
          response?.message ||
          response?.data?.message ||
          'Failed to check in. Please try again.';
        setCheckInError(apiMessage);
      }
    } catch (error) {
      console.error('Check-in error:', error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred while checking in. Please try again.';
      setCheckInError(apiMessage);
    } finally {
      setIsLoading(false);
    }
  }
};

const handleCheckOut = async () => {
  if (isCheckedIn && !isCheckedOut && !isLoading) {
    if (isPastDate(selectedDate)) {
      setCheckInError("Cannot check out for past date");
      return;
    }
    setCheckInError('');
    setIsLoading(true);
    try {
      const attendanceData = {
        user: staff.id,
        type: "checkout",
        event_date: formatDateToDDMMYYYY(selectedDate),
        is_calendar_expansion: getAttendanceExpansionFlag(formatTime(new Date())),
      };
      const response = await saveEmployeeAttendance(attendanceData);

      if (response?.data?.success && response?.data?.data) {
        const updated = response.data.data;

        onCheckOut && onCheckOut(updated);

        // Update UI
        setCheckOutTime(parseTimeStringToDate(updated.checkout_daily));
        setIsCheckedOut(true);
        setIsCheckOutExpanded(
          updated?.is_checkout_calendar_expansion ??
          getAttendanceExpansionFlag(formatTime(new Date()))
        );

        try { window.dispatchEvent(new Event('staffTimingUpdate')); } catch {}
      }
    } catch (error) {
      console.error('Check-out error:', error);
    } finally {
      setIsLoading(false);
    }
  }
};

  const formatTime = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : parseTimeStringToDate(date);
    if (!d) return 'N/A';
    return d.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const appendExpandedSuffix = (timeLabel, isExpanded) => {
    if (!timeLabel || timeLabel === 'N/A') return timeLabel;
    return Number(isExpanded) === 1 ? `${timeLabel} +1` : timeLabel;
  };

  // Extract start time from start_at field (e.g., "2025-10-15 10:00:AM" -> "10:00 AM")
  const formatBreakStartTime = (startAt, isExpanded = 0) => {
    if (!startAt) return 'N/A';
    try {
      // Extract time part from "2025-10-15 13:35:PM"
      const timePart = startAt.split(' ')[1]; // Get "13:35:PM"
      if (!timePart) return 'N/A';
      
      // Parse time and AM/PM
      const timeMatch = timePart.match(/^(\d{1,2}):(\d{2}):(AM|PM)$/i);
      if (!timeMatch) return 'N/A';
      
      let hours = parseInt(timeMatch[1]);
      const minutes = timeMatch[2];
      const period = timeMatch[3].toUpperCase();
      
      // Convert to 24-hour format
      let hour24 = hours;
      
      // If hours >= 13, it's already in 24-hour format, use as-is
      if (hours >= 13) {
        hour24 = hours;
      } else if (period === 'PM' && hours !== 12) {
        hour24 = hours + 12;
      } else if (period === 'AM' && hours === 12) {
        hour24 = 0;
      }
      
      // Format the time in 24-hour format HH:MM
      return appendExpandedSuffix(`${hour24.toString().padStart(2, '0')}:${minutes}`, isExpanded);
    } catch (error) {
      return 'N/A';
    }
  };



  const getIsOutletExtendedForDate = (dateInput) => (
    getOutletExtendedFlagForDate(selectedOutletData?.timing, dateInput || selectedDate)
  );
  const isOutletExtended = getIsOutletExtendedForDate(breakDate || selectedDate);
  const doesScheduleCrossMidnight = useMemo(
    () => doesTimeRangeCrossMidnight(scheduledCheckIn, scheduledCheckOut),
    [scheduledCheckIn, scheduledCheckOut]
  );

  const breakTimeOptions = useMemo(() => (
    buildTimeDropdownOptions({
      isOutletExtended,
      includeNextDay: true,
    }).map((option) => ({
      text: option.label,
      value: option.value,
      timeValue: option.timeValue,
      isNextDay: option.isNextDay,
    }))
  ), [isOutletExtended]);

  const getBreakTimeSelectionMeta = (timeValue) => {
    const parsed = parseExtendedDropdownValue(timeValue);
    if (!parsed.value) {
      return { timeValue: '', isNextDay: false };
    }

    const normalizedScheduleStart = formatTimeToHHMM(scheduledCheckIn);
    const derivedIsNextDay = parsed.isNextDay || (
      isOutletExtended &&
      doesScheduleCrossMidnight &&
      normalizedScheduleStart &&
      parsed.value < normalizedScheduleStart
    );

    return {
      timeValue: parsed.value,
      isNextDay: derivedIsNextDay,
    };
  };

  // Max duration that keeps the break within the current day (can end at exactly 12:00 / 00:00, not past it)
  // e.g. 11:55 PM + 5 min → 12:00 AM is allowed; longer would cross past midnight
  const getMaxBreakDurationMinutes = (timeValue) => {
    const selection = getBreakTimeSelectionMeta(timeValue);
    if (!selection.timeValue) return 0;

    const [hours, minutes] = selection.timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;

    const startMinutesInDay = (hours * 60) + minutes;
    return Math.max(0, (24 * 60) - startMinutesInDay);
  };

  const doesBreakExceedCurrentDay = (timeValue, durationMinutes) => {
    const duration = parseInt(durationMinutes, 10);
    if (!timeValue || Number.isNaN(duration) || duration <= 0) return false;
    return duration > getMaxBreakDurationMinutes(timeValue);
  };

  // True only when the break ends after midnight (ending exactly at 00:00 / 12:00 is allowed)
  const doesBreakCrossPastMidnight = (breakDateTime) => {
    if (!breakDateTime) return false;
    if (breakDateTime.startDate === breakDateTime.endDate) return false;
    return breakDateTime.endTime !== '00:00';
  };

  const getBreakDropdownValue = (timeValue) => {
    const selection = getBreakTimeSelectionMeta(timeValue);
    return getExtendedDropdownSelectedValue({
      value: selection.timeValue,
      anchorTime: scheduledCheckIn,
      isOutletExtended,
      crossesMidnight: doesScheduleCrossMidnight || selection.isNextDay,
    });
  };

  const getBreakDropdownLabel = (timeValue) => {
    const selectedValue = getBreakDropdownValue(timeValue);
    return breakTimeOptions.find(option => option.value === selectedValue)?.text || '';
  };

  const getAttendanceExpansionFlag = (timeValue, dateInput = selectedDate) => {
    const normalizedTime = formatTimeToHHMM(timeValue);
    if (!normalizedTime) return 0;

    const normalizedScheduleStart = formatTimeToHHMM(scheduledCheckIn);
    const normalizedScheduleEnd = formatTimeToHHMM(scheduledCheckOut);
    const selectedValue = getExtendedDropdownSelectedValue({
      value: normalizedTime,
      anchorTime: normalizedScheduleStart,
      isOutletExtended: getIsOutletExtendedForDate(dateInput),
      crossesMidnight: doesTimeRangeCrossMidnight(normalizedScheduleStart, normalizedScheduleEnd),
    });

    return typeof selectedValue === 'string' && selectedValue.endsWith('+1') ? 1 : 0;
  };

  const buildBreakDateTime = (dateString, timeValue, durationMinutes = 0) => {
    if (!dateString) return null;

    const [day, month, year] = dateString.split('-').map(Number);
    if (!day || !month || !year) return null;

    const selection = getBreakTimeSelectionMeta(timeValue);
    if (!selection.timeValue) return null;

    const [hours, minutes] = selection.timeValue.split(':').map(Number);
    const startDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
    if (selection.isNextDay) {
      startDateTime.setDate(startDateTime.getDate() + 1);
    }

    const endDateTime = new Date(startDateTime);
    endDateTime.setMinutes(endDateTime.getMinutes() + (parseInt(durationMinutes, 10) || 0));

    return {
      startDateTime,
      endDateTime,
      startDate: formatDateToDDMMYYYY(startDateTime),
      endDate: formatDateToDDMMYYYY(endDateTime),
      startTime: formatTimeToHHMM(selection.timeValue),
      endTime: formatTimeToHHMM(`${String(endDateTime.getHours()).padStart(2, '0')}:${String(endDateTime.getMinutes()).padStart(2, '0')}`),
      startAbsoluteMinutes: (selection.isNextDay ? 1440 : 0) + (hours * 60) + minutes,
      endAbsoluteMinutes: (selection.isNextDay ? 1440 : 0) + (hours * 60) + minutes + (parseInt(durationMinutes, 10) || 0),
    };
  };

  const buildCrossDayBreakPayloadMeta = ({ startDate, endDate, startTime, endTime }) => {
    const startMinutes = convertToMinutes(startTime);
    const endMinutes = convertToMinutes(endTime);

    // Only split when the break truly spans two calendar dates (e.g. 23:55 → 00:05)
    const midnightPreDuration = (24 * 60) - startMinutes;
    const midnightPostDuration = endMinutes;
    const isMidnightCalendarSplit =
      !!startDate &&
      !!endDate &&
      startDate !== endDate &&
      !Number.isNaN(startMinutes) &&
      !Number.isNaN(endMinutes) &&
      midnightPreDuration > 0 &&
      midnightPostDuration > 0;

    if (isMidnightCalendarSplit) {
      return {
        payloadStartDate: startDate,
        payloadEndDate: endDate,
        cross_day_break_item: [
          {
            start_time: startTime,
            end_time: '00:00',
            duration: midnightPreDuration,
            start_date: startDate,
            end_date: startDate,
          },
          {
            start_time: '00:00',
            end_time: endTime,
            duration: midnightPostDuration,
            start_date: endDate,
            end_date: endDate,
          },
        ],
      };
    }

    return {
      payloadStartDate: startDate,
      payloadEndDate: endDate,
      cross_day_break_item: null,
    };
  };

  // Duration options - 5 minutes to 12 hours with 5-minute intervals
  const generateDurationOptions = () => {
    const options = [];
    // 5 minutes to 12 hours (720 minutes) in 5-minute increments
    for (let minutes = 5; minutes <= 720; minutes += 5) {
      let text;
      if (minutes < 60) {
        // Less than 1 hour: show as "X min"
        text = `${minutes} min`;
      } else {
        // 1 hour or more: show as "Xhr Ymin"
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (remainingMinutes === 0) {
          text = `${hours}hr`;
        } else {
          text = `${hours}hr ${remainingMinutes}min`;
        }
      }
      options.push({ text, value: String(minutes) });
    }
    return options;
  };

  const allDurationOptions = useMemo(() => generateDurationOptions(), []);

  // Hide durations that would push the break past end of the current day
  const durationOptions = useMemo(() => {
    if (!breakTime) return allDurationOptions;
    const maxMinutes = getMaxBreakDurationMinutes(breakTime);
    return allDurationOptions.filter((opt) => parseInt(opt.value, 10) <= maxMinutes);
  }, [allDurationOptions, breakTime, scheduledCheckIn, isOutletExtended, doesScheduleCrossMidnight]);

  // Hide start times that cannot fit the selected duration within the current day
  const availableBreakTimeOptions = useMemo(() => {
    const requiredDuration = parseInt(breakDuration, 10) || 5;
    return breakTimeOptions.filter(
      (opt) => getMaxBreakDurationMinutes(opt.value) >= requiredDuration
    );
  }, [breakTimeOptions, breakDuration, scheduledCheckIn, isOutletExtended, doesScheduleCrossMidnight]);

  // Helper function to format duration consistently
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}hr`;
      } else {
        return `${hours}hr ${remainingMinutes}min`;
      }
    }
  };

  // Check for overlaps with existing bookings and breaks
  const checkOverlaps = () => {
    if (!breakDate || !breakTime || !breakDuration || !staff?.id) {
      setOverlapWarning('');
      return;
    }

    try {
      const breakDateTime = buildBreakDateTime(breakDate, breakTime, breakDuration);
      if (!breakDateTime) {
        setOverlapWarning('');
        return;
      }

      const breakDateObj = breakDateTime.startDateTime;
      const breakStartMinutes = breakDateTime.startDateTime.getHours() * 60 + breakDateTime.startDateTime.getMinutes();
      const breakEndMinutes = breakDateTime.endDateTime.getHours() * 60 + breakDateTime.endDateTime.getMinutes();

      // Check overlaps with existing bookings
      const bookingOverlaps = [];
      therapistBookings.forEach(booking => {
        if (booking.service_at) {
          try {
            // Parse booking time (format is typically "YYYY-MM-DD HH:MM:SS" or similar)
            const parts = booking.service_at.split(' ');
            if (parts.length < 2) return;
            
            const bookingDateStr = parts[0]; // "YYYY-MM-DD" or "DD-MM-YYYY"
            const bookingTimeStr = parts[1]; // "HH:MM:SS" or "HH:MM:AM/PM"
            
            // Convert booking date to Date object
            let bookingDateObj;
            if (bookingDateStr.includes('-')) {
              const dateParts = bookingDateStr.split('-');
              if (dateParts[0].length === 4) {
                // YYYY-MM-DD format
                bookingDateObj = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
              } else {
                // DD-MM-YYYY format
                bookingDateObj = new Date(parseInt(dateParts[2]), parseInt(dateParts[1]) - 1, parseInt(dateParts[0]));
              }
            } else {
              return;
            }

            // Normalize dates for comparison (ignore time)
            const bookingDateNormalized = new Date(bookingDateObj.getFullYear(), bookingDateObj.getMonth(), bookingDateObj.getDate());
            const breakDateNormalized = new Date(breakDateObj.getFullYear(), breakDateObj.getMonth(), breakDateObj.getDate());

            // Check if same date
            if (bookingDateNormalized.getTime() === breakDateNormalized.getTime()) {
              // Parse booking time (format: "HH:MM:SS" or "HH:MM")
              const timeMatch = bookingTimeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?$/i);
              if (!timeMatch) return;
              
              let hours = parseInt(timeMatch[1]);
              const minutes = parseInt(timeMatch[2]);
              const period = timeMatch[4]?.toUpperCase();
              
              // Convert to 24-hour format
              if (period === 'PM' && hours !== 12) {
                hours += 12;
              } else if (period === 'AM' && hours === 12) {
                hours = 0;
              }
              
              const bookingStartMinutes = hours * 60 + minutes;
              const bookingDuration = parseInt(booking.duration) || 30;
              const bookingEndMinutes = bookingStartMinutes + bookingDuration;

              // Check if times overlap
              if (breakStartMinutes < bookingEndMinutes && breakEndMinutes > bookingStartMinutes) {
                bookingOverlaps.push({
                  type: 'booking',
                  service: booking.service,
                  time: bookingTimeStr,
                });
              }
            }
          } catch (error) {
            console.error('Error parsing booking time:', error);
          }
        }
      });

      // Check overlaps with existing breaks (excluding the one being edited)
      const breakOverlaps = [];
      breaksData.forEach(breakItem => {
        if (breakItem.id === editingBreakId) return; // Skip the break being edited
        
        if (breakItem.start_at && breakItem.end_at) {
          try {
            // Parse break date from start_at
            const startAtParts = breakItem.start_at.split(' ');
            if (startAtParts.length >= 2) {
              const breakItemDateStr = startAtParts[0];
              let breakItemDateObj;
              
              if (breakItemDateStr.includes('-')) {
                const dateParts = breakItemDateStr.split('-');
                if (dateParts[0].length === 4) {
                  breakItemDateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                } else {
                  breakItemDateObj = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
                }
              }

              // Check if same date
              if (breakItemDateObj && breakItemDateObj.getTime() === breakDateObj.getTime()) {
                // Parse break start time
                const timePart = startAtParts[1]; // "10:00:AM" or "13:00:PM"
                const timeMatch = timePart.match(/^(\d{1,2}):(\d{2}):(AM|PM)$/i);
                
                if (timeMatch) {
                  let hours = parseInt(timeMatch[1]);
                  const minutes = parseInt(timeMatch[2]);
                  const period = timeMatch[3].toUpperCase();
                  
                  // Convert to 24-hour format
                  if (hours >= 13) {
                    // Already in 24-hour format
                  } else if (period === 'PM' && hours !== 12) {
                    hours += 12;
                  } else if (period === 'AM' && hours === 12) {
                    hours = 0;
                  }
                  
                  const breakItemStartMinutes = hours * 60 + minutes;
                  
                  // Parse end time
                  const endAtParts = breakItem.end_at.split(' ');
                  if (endAtParts.length >= 2) {
                    const endTimePart = endAtParts[1];
                    const endTimeMatch = endTimePart.match(/^(\d{1,2}):(\d{2}):(AM|PM)$/i);
                    
                    if (endTimeMatch) {
                      let endHours = parseInt(endTimeMatch[1]);
                      const endMinutes = parseInt(endTimeMatch[2]);
                      const endPeriod = endTimeMatch[3].toUpperCase();
                      
                      if (endHours >= 13) {
                        // Already in 24-hour format
                      } else if (endPeriod === 'PM' && endHours !== 12) {
                        endHours += 12;
                      } else if (endPeriod === 'AM' && endHours === 12) {
                        endHours = 0;
                      }
                      
                      const breakItemEndMinutes = endHours * 60 + endMinutes;

                      // Check if times overlap
                      if (breakStartMinutes < breakItemEndMinutes && breakEndMinutes > breakItemStartMinutes) {
                        breakOverlaps.push({
                          type: 'break',
                          time: `${timeMatch[1]}:${timeMatch[2]} ${period}`,
                          status: breakItem.status_name || 'Break',
                        });
                      }
                    }
                  }
                }
              }
            }
          } catch (error) {
            console.error('Error parsing break time:', error);
          }
        }
      });

      // Set warning message if overlaps found
      if (bookingOverlaps.length > 0 || breakOverlaps.length > 0) {
        const warnings = [];
        if (bookingOverlaps.length > 0) {
          warnings.push(`Overlaps with ${bookingOverlaps.length} booking(s)`);
        }
        if (breakOverlaps.length > 0) {
          warnings.push(`Overlaps with ${breakOverlaps.length} break(s)`);
        }
        setOverlapWarning(warnings.join(' and '));
      } else {
        setOverlapWarning('');
      }
    } catch (error) {
      console.error('Error checking overlaps:', error);
      setOverlapWarning('');
    }
  };

  // Check for overlaps when break date, time, or duration changes
  useEffect(() => {
    if (isAddBreakMode && staff?.id) {
      checkOverlaps();
    } else {
      setOverlapWarning('');
    }
  }, [breakDate, breakTime, breakDuration, therapistBookings, breaksData, editingBreakId, isAddBreakMode, staff?.id]);

  // Check if break duration exceeds end of day (allowed up to 12:00 / 00:00, not past it)
  const checkDurationExceedsAvailableTime = () => {
    if (!breakTime || !breakDuration || !breakDate) {
      setDurationExceedsError('');
      return;
    }

    try {
      if (doesBreakExceedCurrentDay(breakTime, breakDuration)) {
        setDurationExceedsError(
          'Break cannot exceed the current day. Please choose an earlier time or shorter duration.'
        );
        return;
      }

      const breakDateTime = buildBreakDateTime(breakDate, breakTime, breakDuration);
      if (!breakDateTime) {
        setDurationExceedsError('');
        return;
      }

      if (doesBreakCrossPastMidnight(breakDateTime)) {
        setDurationExceedsError(
          'Break cannot exceed the current day. Please choose an earlier time or shorter duration.'
        );
        return;
      }

      setDurationExceedsError('');
    } catch (error) {
      console.error('Error checking duration exceeds end of day:', error);
      setDurationExceedsError('');
    }
  };

  // Validate duration against end of day when break time, duration, or date changes
  useEffect(() => {
    if (isAddBreakMode && staff?.id) {
      checkDurationExceedsAvailableTime();
    } else {
      setDurationExceedsError('');
    }
  }, [breakTime, breakDuration, breakDate, isAddBreakMode, staff?.id]);

  // If selected duration no longer fits the day after a time change, clamp it
  useEffect(() => {
    if (!isAddBreakMode || !breakTime || !breakDuration) return;

    const maxMinutes = getMaxBreakDurationMinutes(breakTime);
    const currentDuration = parseInt(breakDuration, 10);
    if (Number.isNaN(currentDuration) || currentDuration <= maxMinutes) return;

    const clampedDuration = Math.floor(maxMinutes / 5) * 5;
    if (clampedDuration >= 5) {
      setBreakDuration(String(clampedDuration));
    } else {
      setBreakDuration('');
    }
  }, [breakTime, breakDate, isAddBreakMode]);

  // Date options (today and next 6 days)
  const generateDateOptions = () => {
    const options = [];
    const today = selectedDate || new Date();
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dayName = daysOfWeek[date.getDay()];
      const monthName = months[date.getMonth()];
      const dayNum = date.getDate();
      const text = `${dayName}, ${monthName} ${dayNum}`;
      // Store formatted date as value
      const formattedDate = formatDateToDDMMYYYY(date);
      options.push({ text, value: formattedDate });
    }
    return options;
  };

  // Get display label for selected date
  const getDateDisplayLabel = (dateValue) => {
    if (!dateValue) return '';
    const dateOptions = generateDateOptions();
    const found = dateOptions.find(opt => opt.value === dateValue);
    return found ? found.text : dateValue;
  };

  // Get current time rounded to nearest 5-minute interval in Singapore timezone
  const getCurrentSingaporeTimeRounded = () => {
    // Create date object for current time
    const now = new Date();
    
    // Get Singapore time string
    const sgTimeStr = now.toLocaleString('en-US', { timeZone: 'Asia/Singapore' });
    const sgDate = new Date(sgTimeStr);
    
    const hours = sgDate.getHours();
    const minutes = sgDate.getMinutes();
    
    // Round to nearest 5-minute interval
    const roundedMinutes = Math.round(minutes / 5) * 5;
    
    // Handle minute overflow (e.g., 59 rounds to 60)
    if (roundedMinutes === 60) {
      return `${String((hours + 1) % 24).padStart(2, '0')}:00`;
    }
    
    return `${String(hours).padStart(2, '0')}:${String(roundedMinutes).padStart(2, '0')}`;
  };

  const handleAddBreakClick = () => {
    setIsAddBreakMode(true);
    // Set default date to today
    setBreakDate(formatDateToDDMMYYYY(selectedDate || new Date()));
    // Set default time to current Singapore time rounded
    setBreakTime(getCurrentSingaporeTimeRounded());
    // Set default duration to 5 minutes
    setBreakDuration('5');
    // Set default reason to "Meal & Refreshment"
    setBreakReason('Meal & Refreshment');
    setBreakReasonRemark('');
  };

  const handleEditBreak = (breakItem, index) => {
    setIsAddBreakMode(true);
    setEditingBreakId(breakItem.id); // Store break ID for update
    
    // Pre-fill form with existing data
    // Parse the date from start_at (e.g., "2025-10-15 10:15:AM")
    const startAtParts = breakItem.start_at.split(' ');
    const datePart = startAtParts[0]; // "2025-10-15"
    const timePart = startAtParts[1]; // "10:15:AM"
    
    // Convert date from YYYY-MM-DD to DD-MM-YYYY
    const [year, month, day] = datePart.split('-');
    const formattedDate = Number(breakItem?.is_break_calendar_expenssion) === 1
      ? formatDateToDDMMYYYY(selectedDate)
      : `${day}-${month}-${year}`;
    setBreakDate(formattedDate);

    const startTime24 = convertBreakApiTimeTo24Hour(timePart);
    setBreakTime(
      Number(breakItem?.is_break_calendar_expenssion) === 1 && startTime24
        ? `${startTime24}+1`
        : startTime24
    );
    
    // Calculate duration from start and end times
    if (breakItem.start_at && breakItem.end_at) {
      try {
        const startDateTime = parseBreakApiDateTime(breakItem.start_at);
        const endDateTime = parseBreakApiDateTime(breakItem.end_at);

        if (startDateTime && endDateTime) {
          const diffMins = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);
          if (diffMins >= 0) {
            setBreakDuration(String(diffMins));
          } else {
            setBreakDuration('5'); // Default to 5 minutes if calculation fails
          }
        } else {
          setBreakDuration('5'); // Default to 5 minutes if parsing fails
        }
      } catch (error) {
        console.error('Error calculating duration:', error);
        setBreakDuration('5'); // Default to 5 minutes on error
      }
    }
    
    setBreakReason(breakItem.status_name || '');
    setBreakReasonRemark(breakItem.remark || '');
    
    if (onEditBreak) onEditBreak(breakItem, index);
  };

  const handleDeleteBreak = async (breakItem, index) => {
    // Confirm deletion

    setIsBreakListLoading(true);
    try {
      const response = await deleteBreak(breakItem.id);
      
      if (response?.success || response?.data?.success) {
        //console.log('Break deleted successfully:', response);
        
        // Refresh breaks data to get latest list
        await fetchBreaksData();
        
        // Call parent handler if provided
        if (onDeleteBreak) {
          onDeleteBreak(breakItem, index);
        }
      } else {
        console.error('Break deletion failed:', response);
      }
    } catch (error) {
      console.error('Error deleting break:', error);
    } finally {
      setIsBreakListLoading(false);
    }
  };

  const handleCancelBreak = () => {
    setIsAddBreakMode(false);
    setEditingBreakId(null);
    setBreakDate(null);
    setBreakTime('');
    setBreakDuration('');
    setBreakReason('');
    setBreakReasonRemark('');
    // Reset errors
    setDateError('');
    setTimeError('');
    setDurationError('');
    setReasonError('');
    setDurationExceedsError('');
  };

  const handleSaveBreak = async () => {
    // Reset all errors
    setDateError('');
    setTimeError('');
    setDurationError('');
    setReasonError('');
    setDurationExceedsError('');

    // Validate form
    let hasError = false;
    
    if (!breakDate) {
      setDateError('Date is required');
      hasError = true;
    }
    
    if (!breakTime) {
      setTimeError('Time is required');
      hasError = true;
    }
    
    if (!breakDuration) {
      setDurationError('Duration is required');
      hasError = true;
    }
    
    if (!breakReason) {
      setReasonError('Reason is required');
      hasError = true;
    }

    if (breakTime && breakDuration && doesBreakExceedCurrentDay(breakTime, breakDuration)) {
      setDurationExceedsError(
        'Break cannot exceed the current day. Please choose an earlier time or shorter duration.'
      );
      hasError = true;
    }
    
    if (hasError) {
      return;
    }

    setIsBreakLoading(true);
    try {
      // Ensure breakDate is in correct format (DD-MM-YYYY)
      let formattedDate = breakDate;
      
      // Check if breakDate is in wrong format (contains commas like "Wed, Oct 2025")
      if (breakDate.includes(',')) {
        console.warn('Break date in wrong format:', breakDate);
        // Try to find the correct date from options
        const dateOptions = generateDateOptions();
        const matchedDate = dateOptions.find(opt => opt.text === breakDate);
        if (matchedDate) {
          formattedDate = matchedDate.value;
          // console.log('Corrected date format to:', formattedDate);
        } else {
          // Fallback to today's date
          formattedDate = formatDateToDDMMYYYY(selectedDate || new Date());
          // console.log('Using fallback date:', formattedDate);
        }
      }
      
      const breakDateTime = buildBreakDateTime(formattedDate, breakTime, breakDuration);
      if (!breakDateTime) {
        setTimeError('Time is required');
        setIsBreakLoading(false);
        return;
      }

      if (doesBreakCrossPastMidnight(breakDateTime)) {
        setDurationExceedsError(
          'Break cannot exceed the current day. Please choose an earlier time or shorter duration.'
        );
        setIsBreakLoading(false);
        return;
      }
      
      // Debug logging
      // console.log('Break Date (formatted):', formattedDate);
      // console.log('Break Time:', breakTime);
      // console.log('End Time:', endTime);
      
      // Find the selected reason's id
      const selectedReasonOption = reasonOptions.find(opt => opt.value === breakReason);
      const leaveStatusId = selectedReasonOption ? selectedReasonOption.id : null;
      
      const startsOnExpandedSide = breakDateTime.startAbsoluteMinutes >= 1440;
      // const endsOnExpandedSide = breakDateTime.endAbsoluteMinutes >= 1440;
      // const wholeBreakInExpandedTime =
      //   startsOnExpandedSide &&
      //   endsOnExpandedSide &&
      //   breakDateTime.startDate === breakDateTime.endDate;
      const crossDayBreakMeta = buildCrossDayBreakPayloadMeta({
        startDate: breakDateTime.startDate,
        endDate: breakDateTime.endDate,
        startTime: breakDateTime.startTime,
        endTime: breakDateTime.endTime,
      });
      const payloadStartDate = crossDayBreakMeta.payloadStartDate;
      const payloadEndDate = crossDayBreakMeta.payloadEndDate;
      // const payloadStartDate = wholeBreakInExpandedTime
      //   ? breakDateTime.startDate
      //   : crossDayBreakMeta.payloadStartDate;
      // const payloadEndDate = wholeBreakInExpandedTime
      //   ? breakDateTime.endDate
      //   : crossDayBreakMeta.payloadEndDate;

      // Prepare API payload
      const apiData = {
        start_at: `${payloadStartDate} ${breakDateTime.startTime}`,
        end_at: `${payloadEndDate} ${breakDateTime.endTime}`,
        duration: parseInt(breakDuration),
        employee_id: staff.id,
        outlet_id: selectedOutlet,
        reason: breakReason,
        remark: breakReasonRemark || '',
        is_calendar_expansion: 0,
        cross_day_break_item: crossDayBreakMeta.cross_day_break_item,
      };
      
      // Add leave status id if available (status_id of leave status)
      if (leaveStatusId) {
        apiData.status_id = leaveStatusId;  // status_id of leave status
      }

      const isEditing = editingBreakId !== null;
      // console.log(isEditing ? 'Updating break with data:' : 'Creating break with data:', apiData);

      // Call appropriate API based on whether we're editing or creating
      const response = isEditing 
        ? await updateBreak(editingBreakId, apiData)
        : await createBreak(apiData);

      if (response?.success || response?.data?.success) {
        // console.log(isEditing ? 'Break updated successfully:' : 'Break created successfully:', response);
        
        // Close the form first
        setIsAddBreakMode(false);
        
        // Reset form fields
        setEditingBreakId(null);
        setBreakDate(null);
        setBreakTime('');
        setBreakDuration('');
        setBreakReason('');
        setBreakReasonRemark('');
        
        // Reset errors
        setDateError('');
        setTimeError('');
        setDurationError('');
        setReasonError('');
        setDurationExceedsError('');
        
        // Refresh breaks data
        await fetchBreaksData();
        
        // Call parent handler if provided
        if (onAddBreak) {
          onAddBreak({
            date: breakDate,
            time: breakDateTime.startTime,
            duration: breakDuration,
            reason: breakReason,
            remark: breakReasonRemark,
            isEdit: isEditing
          });
        }
      } else {
        console.error(`Break ${isEditing ? 'update' : 'creation'} failed:`, response);
        const apiMessage =
          response?.message ||
          `Failed to ${isEditing ? 'update' : 'create'} break. Please try again.`;
        setReasonError(apiMessage);
      }
    } catch (error) {
      console.error(`Error ${editingBreakId ? 'updating' : 'creating'} break:`, error);
      const apiMessage =
        error?.response?.message ||
        `An error occurred while ${editingBreakId ? 'updating' : 'creating'} the break.`;
      setReasonError(apiMessage);
    } finally {
      setIsBreakLoading(false);
    }
  };

  if (!staff) return null;

 
  
  return (
    <div className={styles.attendanceContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Therapist Attendance and Break</h2>
        </div>
      </div>
      
      <div className={styles.staffInfo}>
        <div className={styles.infoRow}>
    
          <div className={styles.nameWithPager}>
           <div className={`${styles.genderCircle} ${staff?.gender?.toLowerCase() === "female" ? styles.female : styles.male}`}>
              {staff.pager_number || 'N/A'}
            </div>
            <div className={styles.nameBox}>
            <span className={styles.staffName}>{staff.alias || 'N/A'}</span>
            <span className={styles.label}>{isLoadingSchedule ? 'Checked In at - Loading...' : (checkInTime ? `Checked In at - ${appendExpandedSuffix(formatTime(checkInTime), isCheckInExpanded)}` : 'Not Checked in')}</span>
            {checkOutTime && <span className={styles.label}>Checked Out at - {isLoadingSchedule ? 'Loading...' : (checkOutTime ? appendExpandedSuffix(formatTime(checkOutTime), isCheckOutExpanded) : 'N/A')}</span>}
            </div>

          </div>

          {!checkInTime && <div className={styles.buttonGroup}>
        {(() => {
          const hasIn = Boolean(checkInTime);
          const hasSchedule = Boolean(scheduledCheckIn && scheduledCheckOut);
          const isFuture = isFutureDate(selectedDate);
          const isPast = isPastDate(selectedDate);
          const checkInDisabled = isLoading || hasIn || !hasSchedule || isFuture || isPast;
          const tooltipMessage = isFuture
            ? 'Cannot check in for future date'
            : isPast
              ? "Cannot check in for past date"
              : !hasSchedule
                ? 'Please add therapist schedule.'
                : '';
          
          return (
            <div title={tooltipMessage} className={checkInDisabled ? styles.disabledButtonWrap : undefined}>
              <PrimaryButton 
                text={
                  isLoading 
                    ? "Processing..." 
                    : hasIn 
                      ? `Checked In` 
                      : "Check In"
                } 
                onClick={handleCheckIn}
                className={styles.checkInBtn}
                disabled={checkInDisabled}
              />
            </div>
          );
        })()}
        </div>}

        {checkInTime && <div className={styles.buttonGroup}>
        {(() => {
          const hasIn = Boolean(checkInTime);
          const hasOut = Boolean(checkOutTime);
          const hasSchedule = Boolean(scheduledCheckIn && scheduledCheckOut);
          const isPast = isPastDate(selectedDate);
          const checkOutDisabled = isLoading || !hasIn || hasOut || !hasSchedule || isPast;
          const tooltipMessage = isPast
            ? "Cannot check out for past date"
            : !hasSchedule
              ? 'Please add therapist schedule.'
              : '';
          
          return (
            <div title={tooltipMessage} className={checkOutDisabled ? styles.disabledButtonWrap : undefined}>
              <SecondaryButton 
                text={
                  isLoading 
                    ? "Processing..." 
                    : hasOut 
                      ? `Checked Out` 
                      : "Check Out"
                } 
                onClick={handleCheckOut}
                className={styles.checkOutBtn}
                disabled={checkOutDisabled}
              />
            </div>
          );
        })()}
        </div>}
          
        </div>

        {checkInError ? (
          <div className={styles.checkInError} role="alert">
            {checkInError}
          </div>
        ) : null}

      </div>

      {/* Break Data Section */}
      {!isAddBreakMode && (
        <div className={`${styles.breakSection} ${styles.fadeIn}`}>
          <div className={styles.breakHeader}>
            <h3>Therapist Attendance and Break</h3>
          </div>
          
          {isBreakListLoading ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : breaksData && breaksData.length > 0 ? (
          <div className={styles.breakList}>
            {breaksData.map((breakItem, index) => {
              // New object structure from API
              const statusName = breakItem.status_name || 'N/A';
              const remark = breakItem.remark || 'N/A';
              
              // Calculate duration from start_at and end_at
              const calculateDuration = () => {
                if (breakItem.start_at && breakItem.end_at) {
                  try {
                    const startDateTime = parseBreakApiDateTime(breakItem.start_at);
                    const endDateTime = parseBreakApiDateTime(breakItem.end_at);

                    if (!startDateTime || !endDateTime) {
                      return 'N/A';
                    }

                    const diffMins = Math.round((endDateTime.getTime() - startDateTime.getTime()) / 60000);
                    
                    // Check if result is valid
                    if (isNaN(diffMins) || !isFinite(diffMins) || diffMins < 0) {
                      return 'N/A';
                    }
                    
                    return formatDuration(diffMins);
                  } catch (error) {
                    return 'N/A';
                  }
                }
                return 'N/A';
              };
              
              return (
                <div key={`break-${breakItem.id || breakItem.start_at}-${index}`} className={styles.breakItem}>
                  <div className={styles.breakInfo}>
                    <span className={styles.breakTime}>
                      {breakItem.start_at ? formatBreakStartTime(breakItem.start_at, breakItem?.is_break_calendar_expenssion) : 'N/A'}
                    </span>
                    <span className={styles.breakSeparator}>•</span>
                    <span className={styles.breakDuration}>
                      {calculateDuration()}
                    </span>
                    <span className={styles.breakSeparator}>•</span>
                    <span className={styles.breakType}>{statusName}</span>
                    <span className={styles.breakSeparator}>•</span>
                    <span className={styles.breakReason}>{remark}</span>
                  </div>
                  <div className={styles.breakActions}>
                    <button 
                      className={styles.editBtn} 
                      title="Edit Break"
                      onClick={() => handleEditBreak(breakItem, index)}
                    >
                      <img src={EditIcon} alt="Edit" />
                    </button>
                    <button 
                      className={styles.deleteBtn} 
                      title="Delete Break"
                      onClick={() => handleDeleteBreak(breakItem, index)}
                    >
                      <img src={DeleteIcon} alt="Delete" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No breaks found</p>
            </div>
          )}
          
          <div className={styles.breakFooter}>
            <PrimaryButton 
              text="+ Add Break" 
              onClick={handleAddBreakClick}
              className={styles.addBreakBtn}
              disabled={!scheduledCheckIn || !scheduledCheckOut}
              title={(!scheduledCheckIn || !scheduledCheckOut) ? "Please add therapist schedule." : ""}
            />
            <SecondaryButton 
              text="Close" 
              onClick={() => onClose && onClose()}
              className={styles.closeBtn}
            />
          </div>
        </div>
      )}

      {/* Add/Edit Break Form */}
      {isAddBreakMode && (
        <div className={`${styles.breakSection} ${styles.fadeIn}`}>
          <div className={styles.breakHeader}>
            <h3>Therapist Attendance and Break</h3>
          </div>
          
          <div className={styles.breakForm}>
            <div className={styles.formRow}>
              <div className={styles.fieldWrapper}>
                <DropdownCustomDate
                  options={generateDateOptions()}
                  defaultOption={getDateDisplayLabel(breakDate)}
                  onSelect={(optionText) => {
                    // Find the option by text and get its value
                    const dateOptions = generateDateOptions();
                    const selected = dateOptions.find(opt => opt.text === optionText);
                    if (selected) {
                      // console.log('Date selected - Text:', optionText, 'Value:', selected.value);
                      setBreakDate(selected.value);
                      setDateError(''); // Clear error on selection
                      setDurationExceedsError(''); // Clear duration exceeds error on selection
                    }
                  }}
                  selectedLabel={getDateDisplayLabel(breakDate)}
                  dropdownClass={styles.formDropdown}
                  dropdownToggleClass={styles.dropdownToggle}
                  placeholder="Select date"
                />
              </div>
              
              <div className={styles.fieldWrapper}>
                <DropdownCustomDate
                  options={availableBreakTimeOptions}
                  defaultOption={getBreakDropdownLabel(breakTime)}
                  onSelect={(optionText) => {
                    // Find the option by text and get its value
                    const selected = availableBreakTimeOptions.find(opt => opt.text === optionText);
                    if (selected) {
                      setBreakTime(selected.value);
                      setTimeError(''); // Clear error on selection
                      setDurationExceedsError(''); // Clear duration exceeds error on selection
                    }
                  }}
                  selectedLabel={getBreakDropdownLabel(breakTime)}
                  dropdownClass={styles.formDropdown}
                  dropdownToggleClass={styles.dropdownToggle}
                  placeholder="11:00 AM"
                  scrollToValue={getBreakDropdownValue(breakTime) || getCurrentSingaporeTimeRounded()}
                />
              </div>
              
              <div className={styles.fieldWrapper}>
                <DropdownCustomDate
                  options={durationOptions}
                  defaultOption={breakDuration ? durationOptions.find(d => d.value === breakDuration)?.text : ''}
                  onSelect={(optionText) => {
                    // Find the option by text and get its value
                    const selected = durationOptions.find(opt => opt.text === optionText);
                    if (selected) {
                      setBreakDuration(selected.value);
                      setDurationError(''); // Clear error on selection
                      setDurationExceedsError(''); // Clear duration exceeds error on selection
                    }
                  }}
                  selectedLabel={breakDuration ? (durationOptions.find(d => d.value === breakDuration)?.text || allDurationOptions.find(d => d.value === breakDuration)?.text || `${breakDuration} min`) : ''}
                  dropdownClass={styles.formDropdown}
                  dropdownToggleClass={styles.dropdownToggle}
                  placeholder="30 min"
                />
              </div>
            </div>

            {/* Consolidated Error and Warning Messages */}
            {(dateError || timeError || durationError || durationExceedsError || reasonError || overlapWarning) && (
              <div className={styles.overlapWarning}>
                {overlapWarning && (
                  <div className={styles.warningText}>{overlapWarning}</div>
                )}
                {dateError && (
                  <div className={styles.errorMessage}>{dateError}</div>
                )}
                {timeError && (
                  <div className={styles.errorMessage}>{timeError}</div>
                )}
                {durationError && (
                  <div className={styles.errorMessage}>{durationError}</div>
                )}
                {durationExceedsError && (
                  <div className={styles.errorMessage}>{durationExceedsError}</div>
                )}
                {reasonError && (
                  <div className={styles.errorMessage}>{reasonError}</div>
                )}
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.fieldWrapper}>
                <DropdownReason
                  options={reasonOptions}
                  defaultOption={breakReason}
                  onSelect={(reason) => {
                    setBreakReason(reason);
                    setReasonError(''); // Clear error on selection
                  }}
                  selectedLabel={breakReason}
                  dropdownClass={styles.formDropdownFull}
                  dropdownToggleClass={styles.dropdownToggle}
                  placeholder="Select reason"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <input
                type="text"
                value={breakReasonRemark}
                onChange={(e) => setBreakReasonRemark(e.target.value)}
                placeholder="Reason/Remark (optional)"
                className={styles.remarkInput}
              />
            </div>
          </div>
          
          <div className={styles.breakFooter}>
            <PrimaryButton 
              text={isBreakLoading ? "Saving..." : (editingBreakId ? "Update Break" : "+ Add Break")} 
              onClick={handleSaveBreak}
              className={styles.addBreakBtn}
              disabled={isBreakLoading}
            />
            <SecondaryButton 
              text="Cancel" 
              onClick={handleCancelBreak}
              className={styles.closeBtn}
              disabled={isBreakLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Attendance;